// PN-7 (T-430) — boîte de dialogue « Changer le statut » : la liste du tenant (une seule), la raison demandée pour les sorties
// du parcours. Remplace le menu écrit en dur et le prompt() natif. Elle n'écrit rien : elle rend le choix à `onConfirm(code, raison)`.
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";

export function ChangerStatutDialog({ ouvert, onFermer, statuts, groupes, statutActuel, onConfirm }) {
  const [choix, setChoix] = useState(null);
  const [raison, setRaison] = useState("");
  // T-430b : les entrées sont rangées par étape du pipeline (`groupes`) ; sans groupes, une liste à plat.
  const lignes = groupes || [{ cle: "tous", label: null, replie: false, entrees: statuts }];
  const entree = lignes.flatMap((g) => g.entrees).find((s) => s.code === choix);
  const raisonManquante = !!entree?.raison && !raison.trim();
  const fermer = () => { setChoix(null); setRaison(""); onFermer(); };
  const confirmer = () => { if (!entree || raisonManquante) return; onConfirm(entree.code, entree.raison ? raison.trim() : undefined); fermer(); };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => { if (!o) fermer(); }}>
      <DialogContent data-testid="changer-statut-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Changer le statut du dossier</DialogTitle>
          <DialogDescription>Choisissez la nouvelle étape. Rien n'est effacé : l'historique du dossier garde chaque changement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto" role="radiogroup" aria-label="Statuts du dossier">
          {lignes.map((g) => {
            const liste = (
              <ul className="space-y-1">
                {g.entrees.map((s) => {
                  const actuel = s.code === statutActuel;
                  const choisi = s.code === choix;
                  return (
                    <li key={s.code}>
                      <button type="button" role="radio" aria-checked={choisi} disabled={actuel} data-testid={`statut-option-${s.code}`}
                        onClick={() => setChoix(s.code)}
                        className="w-full text-left text-sm rounded-lg px-3 py-2 min-h-[40px] disabled:opacity-60"
                        style={{
                          color: s.raison ? "var(--oh-error-text)" : "var(--oh-text)",
                          background: choisi ? "var(--oh-accent-bg)" : "transparent",
                          border: `1px solid ${choisi ? "var(--oh-accent)" : "var(--oh-border)"}`,
                        }}>
                        {s.label}{actuel ? " (statut actuel)" : ""}
                      </button>
                    </li>
                  );
                })}
              </ul>
            );
            if (!g.label) return <div key={g.cle}>{liste}</div>;
            if (g.replie) {
              return (
                <details key={g.cle} data-testid="statuts-groupe-autres" className="space-y-1">
                  <summary className="text-sm cursor-pointer min-h-[40px] flex items-center" style={{ color: "var(--oh-text-secondary)" }}>
                    {g.label} ({g.entrees.length})
                  </summary>
                  {liste}
                </details>
              );
            }
            return (
              <div key={g.cle} role="group" aria-label={g.label} data-testid={`statuts-groupe-${g.cle}`} className="space-y-1">
                <p className="text-xs" style={{ color: "var(--oh-text-secondary)", fontWeight: 500 }}>{g.label}</p>
                {liste}
              </div>
            );
          })}
        </div>
        {entree?.raison && (
          <div className="space-y-1">
            <label htmlFor="statut-raison" className="text-sm" style={{ color: "var(--oh-text)" }}>Raison (obligatoire)</label>
            <Textarea id="statut-raison" data-testid="statut-raison" value={raison} onChange={(e) => setRaison(e.target.value)}
              placeholder="Pourquoi ce dossier change-t-il de statut ?" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={fermer} data-testid="statut-annuler">Annuler</Button>
          <Button onClick={confirmer} disabled={!entree || raisonManquante} data-testid="statut-confirmer">Changer le statut</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
